/**
 * IconButtonWithTooltip: A Material-UI IconButton with optional Tooltip.
 * Accepts Material-UI or Custom-SVG icons, with adjustable size.
 */
import React from 'react';
import { Badge, IconButton, Tooltip } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { styled } from '@mui/system';

const TooltipStyled = styled(Tooltip)(() => ({
  '& .MuiTooltip-tooltip': {
    fontSize: '0.85rem',
  },
}));

const IconButtonStyled = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(1),
}));

interface IconButtonWithTooltipProps {
  color?: 'inherit' | 'primary' | 'secondary';
  cypressId?: string;
  disabled?: boolean;
  icon?: React.ReactElement;
  onClick: () => void;
  size?: string | number;
  title?: string;
  ref?: React.Ref<HTMLButtonElement>;
  badgeContent?: string;
  badgeVariantDot?: boolean;
  showBadge?: boolean;
  badgeColor?: 'success' | 'error' | 'warning' | 'info' | 'default';
}

const IconButtonWithTooltip = React.forwardRef<HTMLButtonElement, IconButtonWithTooltipProps>(
  (
    {
      color,
      cypressId,
      disabled = false,
      icon = <ErrorOutlineIcon />,
      onClick,
      size = 22,
      title,
      badgeContent,
      badgeVariantDot = false,
      showBadge = false,
      badgeColor = 'default',
    },
    ref
  ) => {
    const adjustSize = (element: React.ReactElement) => {
      if (!size) return element;
      return React.cloneElement(element, {
        width: size,
        height: size,
      } as Record<string, unknown>);
    };

    const renderedIcon = adjustSize(icon);

    const IconContent = () => {
      if (title) {
        return (
          <TooltipStyled title={title} placement="top">
            <span>{renderedIcon}</span>
          </TooltipStyled>
        );
      } else {
        return renderedIcon;
      }
    };

    return (
      <IconButtonStyled
        color={color}
        data-cy={cypressId || undefined}
        disabled={disabled}
        onClick={onClick}
        ref={ref}
      >
        {showBadge || badgeContent ? (
          <Badge
            badgeContent={badgeVariantDot ? null : badgeContent}
            color={badgeColor === 'default' ? 'secondary' : badgeColor}
            variant={badgeVariantDot ? 'dot' : 'standard'}
            sx={
              badgeColor === 'default'
                ? {
                    '& .MuiBadge-badge': {
                      backgroundColor: '#187787',
                      color: 'white',
                    },
                  }
                : undefined
            }
          >
            {IconContent()}
          </Badge>
        ) : (
          IconContent()
        )}
      </IconButtonStyled>
    );
  }
);

export default IconButtonWithTooltip;
